// System page — subsumes Config, Servers, and Admin Users under tabbed UI.
// Old routes (/config, /servers, /admin/users) redirect here with ?tab=.
//
// Config tab is gated on activeVpsId (requires an entered server).
// Users tab is admin-only.

import { useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { VpsListPage } from "./VpsListPage";
import { AdminUsersPage } from "./AdminUsersPage";
import { ConfigPage } from "./ConfigPage";

const TABS = [
  { key: "servers", label: "Servers" },
  { key: "config", label: "Config" },
  { key: "users", label: "Users" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export function SystemPage() {
  const { role, activeVpsId } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get("tab") as TabKey | null) ?? "servers";

  const isAdmin = role === "admin";
  const hasActiveVps = activeVpsId !== null;

  // Determine if a tab should be enabled
  function tabDisabled(key: TabKey): boolean {
    if (key === "config") return !hasActiveVps;
    if (key === "users") return !isAdmin;
    return false;
  }

  // Resolve the effective tab — fall back to first available if current is
  // not accessible.
  const effectiveTab: TabKey = (() => {
    if (!tabDisabled(tab)) return tab;
    const fallback = TABS.find((t) => !tabDisabled(t.key));
    return fallback?.key ?? "servers";
  })();

  function switchTab(key: TabKey) {
    setSearchParams({ tab: key }, { replace: true });
  }

  return (
    <>
      {/* Flat tab bar — no extra layout wrapper; child pages have their own */}
      <div className="row wrap" style={{ marginBottom: 4, gap: 2, padding: "10px 20px 0" }}>
        {TABS.map((t) => {
          const disabled = tabDisabled(t.key);
          return (
            <button
              key={t.key}
              className={effectiveTab === t.key ? "primary" : ""}
              disabled={disabled}
              style={{ padding: "2px 10px", fontSize: 11 }}
              onClick={() => switchTab(t.key)}
              title={
                disabled
                  ? t.key === "config"
                    ? "Select a server first"
                    : "Admin only"
                  : undefined
              }
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {effectiveTab === "servers" && <VpsListPage />}
      {effectiveTab === "config" && hasActiveVps && <ConfigPage />}
      {effectiveTab === "config" && !hasActiveVps && (
        <div className="app-main">
          <div className="card">
            <div className="muted small">Select a server from the Servers tab to configure it.</div>
          </div>
        </div>
      )}
      {effectiveTab === "users" && isAdmin && <AdminUsersPage />}
      {effectiveTab === "users" && !isAdmin && (
        <div className="app-main">
          <div className="card">
            <div className="muted small">Admin access required.</div>
          </div>
        </div>
      )}
    </>
  );
}